import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Menu, MenuItem } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { useListCurrentSolutionAssetOptions } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { CurrentSolutionAssetOption } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { useContext, useState } from "react";

type Props = {
  readonly onAssetPick: (asset: CurrentSolutionAssetOption) => void;
};

export const CurrentSolutionPrefillAsset = ({ onAssetPick }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { data: assetOptions } = useListCurrentSolutionAssetOptions(analysisId, versionId);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const handleAssetPick = (asset: CurrentSolutionAssetOption) => {
    closeMenu();
    onAssetPick(asset);
  };

  const getAssetOptionLabel = (asset: CurrentSolutionAssetOption) => {
    const labelParts: string[] = [
      t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions[asset.investmentToolType]),
      asset.name,
    ];

    const amountValue = asset.amount?.value;
    const regularAmountValue = asset.regularAmount?.value;

    if (amountValue !== undefined && amountValue !== null) {
      labelParts.push(`${formatCurrency(amountValue, asset.amount?.currencyCode)}`);
    } else if (regularAmountValue !== undefined && regularAmountValue !== null) {
      labelParts.push(`${formatCurrency(regularAmountValue, asset.regularAmount?.currencyCode)}/${t(Resources.Common.Month)}`);
    }

    return labelParts.filter(Boolean).join(" - ");
  };

  return (
    <>
      <Flex justifyContent="flex-end">
        <Button
          variant="text"
          onClick={openMenu}
          endIcon={
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                color: "primary.main",
                transform: isMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          }
        >
          {t(Resources.AnalysisDetail.GoalsFulfillment.LoadCurrentSolution)}
        </Button>
      </Flex>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { border: "1px solid", borderColor: "divider" } } }}
      >
        {(assetOptions?.items ?? []).map((asset) => {
          const label = getAssetOptionLabel(asset);
          return (
            <MenuItem key={`${asset.virtualKey}`} onClick={() => handleAssetPick(asset)}>
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};
